import type { ccv3 } from '@proj-airi/ccc'

import { useLocalStorageManualReset } from '@proj-airi/stage-shared/composables'
import { useAiriCardStore } from '@proj-airi/stage-ui/stores/modules/airi-card'

import seoyoonCard from '../constants/seoyoon-card.json'

/**
 * Marks that the bundled tutor card has been seeded.
 *
 * Persisted rather than derived from "is the card present?" so that a student
 * who deletes 서윤 선생님 or switches to another character does not get her
 * silently reinstalled and re-selected on the next launch.
 */
const seededKey = 'aituter/default-character-seeded'

/**
 * Installs 서윤 선생님 as the active character on first launch.
 *
 * AIRI seeds its own built-in card (`ReLU`, id `default`) and defaults the
 * stage to Live2D Hiyori. For a tutoring app both are wrong out of the box, and
 * expecting the student to import a character card before their first lesson is
 * not a reasonable first-run experience.
 *
 * Activating the card is enough to move the stage model too: the card store
 * watches the active card and applies its `displayModelId` — `preset-vrm-1`,
 * AvatarSample_A — to the stage model setting.
 *
 * Must run after {@link useAiriCardStore}`.initialize()`, which repairs a
 * dangling `activeCardId`; seeding before that would have its selection
 * overwritten.
 *
 * Idempotent. Returns the seeded card id, or `null` when seeding was skipped.
 */
export function installDefaultCharacter(): string | null {
  const seeded = useLocalStorageManualReset<boolean>(seededKey, false)
  if (seeded.value)
    return null

  const cardStore = useAiriCardStore()

  try {
    const cardId = cardStore.addCard(seoyoonCard as unknown as ccv3.CharacterCardV3, 'import')
    cardStore.activeCardId = cardId
    seeded.value = true

    return cardId
  }
  catch (error) {
    // NOTICE:
    // Seeding runs inside App.vue's onMounted, ahead of the server channel,
    // context bridge, and stage model initialisation. An exception here used to
    // abort that whole sequence, so a malformed card silently half-started the
    // app. Card parsing is strict — `newAiriCard` rejects any `mes_example`
    // line that is not prefixed `{{user}}:` or `{{char}}:`, including the empty
    // line a trailing newline produces — and it is not worth taking startup
    // down with it.
    // `seeded` stays false so a fixed card is picked up on the next launch.
    // Source/context: packages/stage-ui/src/stores/modules/airi-card.ts newAiriCard.
    console.error('[aituter] Failed to seed the default character:', error)
    return null
  }
}
